import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, CircleAlert, CircleHelp, KeyRound, ShieldCheck, Star } from 'lucide-react';
import clsx from 'clsx';
import { apiClient, ApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';
import { DeleteWithCaptchaModal } from '@/components/ui/delete-captcha';
import type { DeleteCaptchaProof } from '@/lib/captcha-api';
import type { DeepseekKeyStatus, DeepseekKeyVerifyResult } from '@/lib/types';

/**
 * Issue #343 — 管理员「系统设置 → AI文本分析 → DeepSeek 密钥」多 Key 管理卡片。
 *
 * 管理员确认口径：多 Key **不用于提升并发**，只用于轮换 / 故障隔离 / 审计 / 可控切换。
 * 运行时始终只用「默认且启用」的一把 key（默认被停用时按顺序回退第一把启用）。
 *
 * 红线：
 *   - 永不回显 key 明文：列表只显示别名 / 启停 / 是否默认 / 更新时间 / 操作者；
 *   - 新增输入框仅用于写入新 key，保存成功后立即清空本地状态、绝不持久化；
 *   - 删除属「删除」红线，需通过后端图形验证码（deepseek-credential:remove）；
 *   - 仅 ADMIN/SUPER_ADMIN 可用（后端 RBAC 双重拦截）。
 */

const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  return err instanceof Error ? err.message : '操作失败，请稍后重试。';
}

/**
 * Issue #366 — key 校验状态徽章（脱敏）:明确区分「密钥无效(需更新 Key)」与「暂时无法确认
 * (网络/服务波动,可重试)」,避免管理员把新旧 key 失败混为一谈。
 */
const VERIFY_BADGE: Record<
  DeepseekKeyStatus['verifyStatus'],
  { label: string; className: string; Icon: typeof BadgeCheck }
> = {
  valid: { label: '已验证有效', className: 'bg-emerald-50 text-emerald-700', Icon: BadgeCheck },
  invalid: { label: '密钥无效', className: 'bg-rose-50 text-rose-700', Icon: CircleAlert },
  error: { label: '暂未通过', className: 'bg-amber-50 text-amber-700', Icon: CircleHelp },
  unverified: { label: '未验证', className: 'bg-slate-100 text-slate-500', Icon: CircleHelp },
};

export function DeepseekCredentialSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [alias, setAlias] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; alias: string } | null>(null);
  // Issue #366:正在校验的 key id + 最近一次校验结果横幅(脱敏)。
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<DeepseekKeyVerifyResult | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'deepseek', 'credentials'],
    enabled: canEdit,
    queryFn: () => apiClient.get<DeepseekKeyStatus[]>('/admin/deepseek/credentials'),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'deepseek', 'credentials'] });

  const addMutation = useMutation({
    mutationFn: (body: { alias: string; apiKey: string; makeDefault: boolean }) =>
      apiClient.post<{ id: string }>('/admin/deepseek/credentials', body),
    onSuccess: async () => {
      setError(null);
      // 安全红线：保存后立即清空本地明文草稿，绝不持久化。
      setAlias('');
      setApiKey('');
      setMakeDefault(false);
      setNote('已新增 DeepSeek Key，运行时即时生效。');
      await refresh();
    },
    onError: (err) => {
      setNote(null);
      setError(errMessage(err));
    },
  });

  const patchMutation = useMutation({
    mutationFn: (args: { id: string; body?: { enabled?: boolean; alias?: string }; setDefault?: boolean }) =>
      args.setDefault
        ? apiClient.patch<unknown>(`/admin/deepseek/credentials/${args.id}/default`)
        : apiClient.patch<unknown>(`/admin/deepseek/credentials/${args.id}`, args.body ?? {}),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => {
      setNote(null);
      setError(errMessage(err));
    },
  });

  // Issue #366:保存后就地校验一把 key(实时探测 provider)。响应脱敏,绝不含明文。
  const verifyMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ result: DeepseekKeyVerifyResult; status: DeepseekKeyStatus[] }>(
        `/admin/deepseek/credentials/${id}/verify`,
      ),
    onSuccess: async (data) => {
      setNote(null);
      setError(null);
      setVerifyResult(data.result);
      await refresh();
    },
    onError: (err) => {
      setNote(null);
      setVerifyResult(null);
      setError(errMessage(err));
    },
    onSettled: () => setVerifyingId(null),
  });

  async function runRemove(proof: DeleteCaptchaProof) {
    if (!removeTarget) return;
    await apiClient.request<unknown>(`/admin/deepseek/credentials/${removeTarget.id}`, {
      method: 'DELETE',
      body: JSON.stringify(proof),
    });
    setError(null);
    setNote(`已删除 DeepSeek Key「${removeTarget.alias}」。`);
    await refresh();
  }

  if (!canEdit) {
    return (
      <Card title="DeepSeek 密钥">
        <p className="text-sm text-slate-500">DeepSeek 密钥管理仅 ADMIN / SUPER_ADMIN 可用。</p>
      </Card>
    );
  }

  const keys = query.data ?? [];
  const busy = addMutation.isPending || patchMutation.isPending || verifyMutation.isPending;

  return (
    <Card
      title="DeepSeek 密钥"
      description="管理 DeepSeek API Key（新增 / 停用 / 标记默认 / 轮换 / 删除）。服务端 AES-256-GCM 加密存储，运行时即时生效（无需重启），界面永不回显明文。多 Key 仅用于轮换 / 故障隔离 / 审计 / 可控切换，不用于提升并发。未配置任何 Key 时回退环境变量 DEEPSEEK_API_KEY；两者都缺失时生产环境评分明确失败，不返回假结果。"
    >
      {query.isLoading ? (
        <LoadingState label="正在加载 DeepSeek 密钥状态…" />
      ) : query.isError ? (
        <ErrorState
          title="DeepSeek 密钥状态加载失败"
          description={errMessage(query.error)}
          retry={() => void query.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-5">
          <section className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <KeyRound className="h-4 w-4 text-slate-400" /> 新增 Key
            </h3>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1.6fr]">
              <input
                className={INPUT}
                placeholder="别名（如「主账号」，可空）"
                maxLength={64}
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                data-testid="deepseek-key-alias"
              />
              <input
                className={INPUT}
                type="password"
                autoComplete="new-password"
                placeholder="DeepSeek API Key（明文仅用于加密落库，绝不回显）"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                data-testid="deepseek-key-secret"
              />
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                data-testid="deepseek-key-make-default"
              />
              设为默认（等价「轮换」：新增并切换默认；第一把 Key 恒为默认）
            </label>
            <div className="mt-3">
              <button
                type="button"
                className={PRIMARY_BTN}
                disabled={busy || apiKey.trim().length === 0}
                onClick={() => {
                  setNote(null);
                  setError(null);
                  addMutation.mutate({ alias: alias.trim(), apiKey: apiKey.trim(), makeDefault });
                }}
                data-testid="deepseek-key-add"
              >
                {addMutation.isPending ? '保存中…' : '新增 Key'}
              </button>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">已配置的 Key</h3>
            {keys.length === 0 ? (
              <p className="text-xs text-slate-400">
                暂无 DeepSeek Key。当前运行时回退环境变量 DEEPSEEK_API_KEY（如已配置）。
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {keys.map((k) => (
                  <li
                    key={k.id}
                    className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-800">
                        {k.alias || '（未命名）'}
                        {k.isDefault ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                            <Star className="h-3 w-3" /> 默认
                          </span>
                        ) : null}
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                            k.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500',
                          )}
                        >
                          <ShieldCheck className="h-3 w-3" /> {k.enabled ? '启用' : '停用'}
                        </span>
                        {/* Issue #366:最近一次校验状态徽章(脱敏)。 */}
                        {(() => {
                          const b = VERIFY_BADGE[k.verifyStatus];
                          return (
                            <span
                              className={clsx(
                                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                                b.className,
                              )}
                              data-testid={`deepseek-key-verify-status-${k.id}`}
                            >
                              <b.Icon className="h-3 w-3" /> {b.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        更新：{fmtTime(k.updatedAt)} · 操作者 {k.updatedByName || k.updatedBy || '—'}
                      </div>
                      {k.verifyStatus !== 'unverified' ? (
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          校验：{fmtTime(k.verifiedAt)}
                          {k.verifySummary ? <span className="text-slate-500"> · {k.verifySummary}</span> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Issue #366:保存后就地验证该 key 是否有效(实时探测,不做真实评分)。 */}
                      <button
                        type="button"
                        className={SECONDARY_BTN}
                        disabled={busy}
                        onClick={() => {
                          setNote(null);
                          setError(null);
                          setVerifyResult(null);
                          setVerifyingId(k.id);
                          verifyMutation.mutate(k.id);
                        }}
                        data-testid={`deepseek-key-verify-${k.id}`}
                      >
                        {verifyingId === k.id && verifyMutation.isPending ? '验证中…' : '验证'}
                      </button>
                      {!k.isDefault && k.enabled ? (
                        <button
                          type="button"
                          className={SECONDARY_BTN}
                          disabled={busy}
                          onClick={() => {
                            setNote(null);
                            setError(null);
                            patchMutation.mutate({ id: k.id, setDefault: true });
                          }}
                        >
                          设为默认
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={SECONDARY_BTN}
                        disabled={busy}
                        onClick={() => {
                          setNote(null);
                          setError(null);
                          patchMutation.mutate({ id: k.id, body: { enabled: !k.enabled } });
                        }}
                      >
                        {k.enabled ? '停用' : '启用'}
                      </button>
                      <button
                        type="button"
                        className={SECONDARY_BTN}
                        disabled={busy}
                        onClick={() => {
                          setNote(null);
                          setError(null);
                          setRemoveTarget({ id: k.id, alias: k.alias || '（未命名）' });
                        }}
                      >
                        删除
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Issue #366:最近一次校验结果横幅——明确区分「密钥无效(需更新 Key)」与「暂时无法确认(可重试)」。 */}
          {verifyResult ? (
            <div
              className={clsx(
                'rounded-md border px-3 py-2 text-xs',
                verifyResult.status === 'valid'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : verifyResult.status === 'invalid'
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-amber-200 bg-amber-50 text-amber-700',
              )}
              data-testid="deepseek-key-verify-result"
            >
              {verifyResult.summary}
            </div>
          ) : null}
          {note ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {note}
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      )}

      {removeTarget ? (
        <DeleteWithCaptchaModal
          key={`deepseek-remove-${removeTarget.id}`}
          action="deepseek-credential:remove"
          title={`删除 DeepSeek Key · ${removeTarget.alias}`}
          description="删除后该 Key 立即从运行时选择中移除（如为默认将自动提升其它 Key）。删除前需通过图形验证码防误删。"
          confirmLabel="确认删除"
          onClose={() => setRemoveTarget(null)}
          onConfirm={runRemove}
        />
      ) : null}
    </Card>
  );
}
